/**
 * Artifact Service — Saves and loads code artifacts (schemas, meta tags, scripts, etc.)
 * to Supabase card_audit_results with a special shortcut_id prefix "artifact-*".
 */

import { supabase } from "@/lib/supabaseClient";

export type ArtifactType =
  // classic
  | "schema" | "faq" | "meta" | "title" | "h1" | "content" | "technical" | "entity" | "competitor"
  // card-specific (Gap 2)
  | "canonical" | "redirect" | "robots" | "llms" | "sitemap" | "alt" | "disavow" | "local"
  | "review" | "csv" | "brief"
  // legacy raw code kinds
  | "css" | "js" | "html";

export interface Artifact {
  id?: string;
  domain: string;
  cardId: string;
  type: ArtifactType;
  title: string;
  description: string;
  code: string;
  language: string;
  targetUrl?: string;
  sourceAuditId?: string;
  created_at?: string;
}

export async function saveArtifact(artifact: Artifact, userId?: string): Promise<string | null> {
  try {
    if (!userId) {
      console.warn("[ArtifactService] No authenticated user");
      return null;
    }

    const { data, error } = await supabase
      .from("card_audit_results")
      .insert({
        user_id: userId,
        domain: artifact.domain,
        card_id: artifact.cardId,
        shortcut_id: `artifact-${artifact.type}`,
        edge_function: "ruby-ai-code-generator",
        region: "",
        form_data: { targetUrl: artifact.targetUrl, sourceAuditId: artifact.sourceAuditId },
        structured_data: {
          type: artifact.type,
          title: artifact.title,
          code: artifact.code,
          language: artifact.language,
          targetUrl: artifact.targetUrl,
        },
        analysis_text: artifact.description,
        recommendations: [],
        processing_time: 0,
        sources: { claude: true },
      })
      .select("id")
      .single();

    if (error) {
      console.error("[ArtifactService] Save error:", error.message);
      return null;
    }

    console.log(`✅ [ArtifactService] Saved artifact ${data.id}: ${artifact.title}`);
    return data.id;
  } catch (err) {
    console.error("[ArtifactService] Unexpected error:", err);
    return null;
  }
}

export async function loadArtifacts(params: {
  userId?: string;
  cardId?: string;
  type?: string;
  limit?: number;
}): Promise<Artifact[]> {
  try {
    if (!params.userId) return [];

    let query = supabase
      .from("card_audit_results")
      .select("id, domain, card_id, shortcut_id, structured_data, analysis_text, form_data, created_at")
      .eq("user_id", params.userId)
      .like("shortcut_id", "artifact-%")
      .order("created_at", { ascending: false })
      .limit(params.limit || 50);

    if (params.cardId) query = query.eq("card_id", params.cardId);
    if (params.type) query = query.eq("shortcut_id", `artifact-${params.type}`);

    const { data, error } = await query;
    if (error || !data) return [];

    return data.map((row: any) => ({
      id: row.id,
      domain: row.domain,
      cardId: row.card_id,
      type: row.structured_data?.type || row.shortcut_id?.replace("artifact-", "") || "html",
      title: row.structured_data?.title || "Untitled",
      description: row.analysis_text || "",
      code: row.structured_data?.code || "",
      language: row.structured_data?.language || "html",
      targetUrl: row.structured_data?.targetUrl || "",
      sourceAuditId: row.form_data?.sourceAuditId || "",
      created_at: row.created_at,
    }));
  } catch (err) {
    console.error("[ArtifactService] Load error:", err);
    return [];
  }
}

export async function deleteArtifact(artifactId: string): Promise<boolean> {
  try {
    const { error } = await supabase.from("card_audit_results").delete().eq("id", artifactId);
    return !error;
  } catch {
    return false;
  }
}

const ARTIFACT_AUDIT_TYPES = new Set([
  "full-seo-audit", "technical-seo", "page-level-audit", "content-gaps",
  "brand-audit", "visual-identity", "voice-personality",
  "serp-analysis", "aeo-strategy",
]);

export function shouldOfferQuickFixes(auditType: string): boolean {
  return ARTIFACT_AUDIT_TYPES.has(auditType);
}

/**
 * Generate Quick Fix suggestions from an audit result.
 * Called client-side to parse the audit and suggest fixable items.
 */
export function generateQuickFixes(auditResult: any): Artifact["type"] extends string ? Array<{
  id: string; type: string; title: string; description: string; targetUrl?: string; estimatedTime: string; priority: string;
}> : never {
  const fixes: any[] = [];
  const domain = auditResult.domain || "";

  if (auditResult.technicalSEO) {
    const tech = auditResult.technicalSEO;
    if (!tech.hasSchema) {
      fixes.push({
        id: `fix-schema-${domain}`,
        type: "schema",
        title: "Generate Schema Markup",
        description: `Add structured data to improve search visibility for ${domain}`,
        targetUrl: `https://${domain}/`,
        estimatedTime: "~2 min",
        priority: "high",
      });
    }
    if (tech.metaDescription && !tech.metaDescription.optimized) {
      fixes.push({
        id: `fix-meta-${domain}`,
        type: "meta",
        title: "Fix Meta Description",
        description: `Current meta description is ${tech.metaDescription.length} chars — optimize for better CTR`,
        targetUrl: `https://${domain}/`,
        estimatedTime: "~30 sec",
        priority: "medium",
      });
    }
    if (tech.titleTag && !tech.titleTag.optimized) {
      fixes.push({
        id: `fix-title-${domain}`,
        type: "meta",
        title: "Optimize Title Tag",
        description: `Current title is ${tech.titleTag.length} chars — improve for rankings`,
        targetUrl: `https://${domain}/`,
        estimatedTime: "~30 sec",
        priority: "medium",
      });
    }
    if (tech.images && tech.images.missingAlt > 0) {
      fixes.push({
        id: `fix-alt-${domain}`,
        type: "html",
        title: "Generate Alt Text",
        description: `${tech.images.missingAlt} images missing alt text — generate descriptive alt attributes`,
        targetUrl: `https://${domain}/`,
        estimatedTime: "~1 min",
        priority: "medium",
      });
    }
  }

  if (auditResult.serpFeatures?.length > 0) {
    const hasFAQ = auditResult.serpFeatures.some((sf: any) => sf.serpFeatures?.includes("FAQ") || sf.serpFeatures?.includes("People Also Ask"));
    if (hasFAQ) {
      fixes.push({
        id: `fix-faq-schema-${domain}`,
        type: "schema",
        title: "Generate FAQ Schema",
        description: "Add FAQ structured data to capture People Also Ask SERP features",
        targetUrl: `https://${domain}/`,
        estimatedTime: "~2 min",
        priority: "high",
      });
    }
  }

  if (auditResult.keywords?.length > 0 && !auditResult.technicalSEO?.hasSchema) {
    fixes.push({
      id: `fix-article-schema-${domain}`,
      type: "schema",
      title: "Generate Article Schema",
      description: "Add Article structured data for better indexing and rich results",
      targetUrl: `https://${domain}/`,
      estimatedTime: "~1 min",
      priority: "low",
    });
  }

  return fixes.slice(0, 5) as any;
}
